import { lazy, Suspense, type ReactNode } from 'react';
import { useApp, type Panel } from '../../store';
import { AuthPanel } from '../../components/AuthPanel';
import { TerritoryPanel } from '../../components/TerritoryPanel';
import { ProfilePanel } from '../../components/ProfilePanel';
import { ShortcutsPanel } from '../../components/ShortcutsPanel';
import { PoiPanel } from '../../components/PoiPanel';
import { TutorialOverlay } from '../../components/TutorialOverlay';
import { NewsOverlay } from '../../components/NewsOverlay';
import { RevealToast } from '../../components/RevealToast';
import { ErrorBoundary } from '../../components/ErrorBoundary';

const LeaderboardPanel = lazy(() =>
  import('../../components/LeaderboardPanel').then((m) => ({ default: m.LeaderboardPanel })),
);
const EmpirePanel = lazy(() =>
  import('../../components/EmpirePanel').then((m) => ({ default: m.EmpirePanel })),
);
const AlliancePanel = lazy(() =>
  import('../../components/AlliancePanel').then((m) => ({ default: m.AlliancePanel })),
);
const AdminPanel = lazy(() =>
  import('../../components/AdminPanel').then((m) => ({ default: m.AdminPanel })),
);
const ResourcesPanel = lazy(() =>
  import('../../components/ResourcesPanel').then((m) => ({ default: m.ResourcesPanel })),
);
const TechnologiesPanel = lazy(() =>
  import('../../components/TechnologiesPanel').then((m) => ({ default: m.TechnologiesPanel })),
);
const ChatPanel = lazy(() =>
  import('../../components/ChatPanel').then((m) => ({ default: m.ChatPanel })),
);
const BuildingsPanel = lazy(() =>
  import('../../components/BuildingsPanel').then((m) => ({ default: m.BuildingsPanel })),
);
const SuggestionsPanel = lazy(() =>
  import('../../components/SuggestionsPanel').then((m) => ({ default: m.SuggestionsPanel })),
);
const UnPanel = lazy(() =>
  import('../../components/UnPanel').then((m) => ({ default: m.UnPanel })),
);
const MarketPanel = lazy(() =>
  import('../../components/MarketPanel').then((m) => ({ default: m.MarketPanel })),
);

function LazyPanel({ children }: { children: ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

/** Contenu du panneau actif (sans overlays). */
export function ActiveGamePanel({ panel }: { panel: Panel }) {
  const { user, selectedPoiId, setPanel, setSelectedPoi } = useApp();

  switch (panel) {
    case 'auth':
      return <AuthPanel />;
    case 'territory':
      return <TerritoryPanel />;
    case 'profile':
      return user ? <ProfilePanel /> : null;
    case 'shortcuts':
      return user ? <ShortcutsPanel /> : null;
    case 'leaderboard':
      return (
        <LazyPanel>
          <LeaderboardPanel />
        </LazyPanel>
      );
    case 'chat':
      return user ? (
        <LazyPanel>
          <ChatPanel />
        </LazyPanel>
      ) : null;
    case 'empire':
      return user ? (
        <LazyPanel>
          <EmpirePanel />
        </LazyPanel>
      ) : null;
    case 'resources':
      return user ? (
        <LazyPanel>
          <ResourcesPanel />
        </LazyPanel>
      ) : null;
    case 'technologies':
      return user ? (
        <LazyPanel>
          <TechnologiesPanel />
        </LazyPanel>
      ) : null;
    case 'buildings':
      return user ? (
        <LazyPanel>
          <BuildingsPanel />
        </LazyPanel>
      ) : null;
    case 'alliances':
      return user ? (
        <LazyPanel>
          <AlliancePanel />
        </LazyPanel>
      ) : null;
    case 'admin':
      return user?.isAdmin ? (
        <LazyPanel>
          <AdminPanel />
        </LazyPanel>
      ) : null;
    case 'suggestions':
      return user ? (
        <LazyPanel>
          <SuggestionsPanel />
        </LazyPanel>
      ) : null;
    case 'onu':
      return user ? (
        <LazyPanel>
          <UnPanel />
        </LazyPanel>
      ) : null;
    case 'market':
      return user ? (
        <LazyPanel>
          <MarketPanel />
        </LazyPanel>
      ) : null;
    case 'poi':
      return selectedPoiId != null ? (
        <aside
          data-panel-root="poi"
          className="absolute inset-x-0 bottom-0 z-30 flex max-h-[78vh] flex-col overflow-hidden rounded-t-3xl border border-[var(--line)] bg-[var(--bg-panel)] shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:right-4 sm:top-24 sm:bottom-4 sm:w-[24rem] sm:rounded-3xl"
        >
          <PoiPanel
            poiId={selectedPoiId}
            onClose={() => {
              setPanel('none');
              setSelectedPoi(null);
            }}
          />
        </aside>
      ) : null;
    default:
      return null;
  }
}

export function GameOverlays() {
  const { user, tutorialOpen, newsOpen } = useApp();
  return (
    <>
      <RevealToast />
      {tutorialOpen && user && !user.needsEmpireName && <TutorialOverlay key="tutorial" />}
      {newsOpen && user && !user.needsEmpireName && !tutorialOpen && <NewsOverlay key="news" />}
    </>
  );
}

/** Rendu V1 : panneaux + overlays (comportement historique). */
export function GamePanels() {
  const { panel } = useApp();
  return (
    <>
      <ErrorBoundary label="panel" key={panel}>
        <ActiveGamePanel panel={panel} />
      </ErrorBoundary>
      <GameOverlays />
    </>
  );
}
